import React from 'react';
import { Navigate, NavLink, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { get, post } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDonate } from '../../contexts/PublicDonateContext';
import { formatCurrency } from '../portal/portalDonateShared';
import { cardFeeRadioSuffix, computeClientFeeTotal, mergedFeeDisplay } from '../../utils/donationFeeConfig';
import DonateCheckoutSignInDialog from '../../components/donate/DonateCheckoutSignInDialog';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = Boolean(RECAPTCHA_SITE_KEY && String(RECAPTCHA_SITE_KEY).trim());

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function buildBillingPayload(b) {
  return {
    first_name: String(b.first_name || '').trim(),
    last_name: String(b.last_name || '').trim(),
    email: String(b.email || '').trim().toLowerCase(),
    phone: String(b.phone || '').trim(),
    address_1: String(b.address_1 || '').trim(),
    address_2: String(b.address_2 || '').trim(),
    city: String(b.city || '').trim(),
    state: String(b.state || '').trim(),
    postcode: String(b.postcode || '').trim(),
    country: String(b.country || 'US').trim() || 'US',
  };
}

function formatMoney(n) {
  const x = Number(n || 0);
  return `$${x.toFixed(2)}`;
}

/** Line items + subtotal (same fields as cart / kiosk-style checkout summary). */
function donateCheckoutLineTotal(item) {
  const qty = Math.max(1, parseInt(item?.quantity, 10) || 1);
  return Number(item?.unit_price || 0) * qty;
}

function coerceMoney(x) {
  const n = typeof x === 'number' && Number.isFinite(x) ? x : Number(x);
  return Number.isFinite(n) ? round2(n) : NaN;
}

function DonateCheckoutOrderLines({ cart, totalAmount, summaryAmounts, feeName }) {
  if (!Array.isArray(cart) || cart.length === 0) return null;
  const fromLines = cart.reduce((s, i) => s + donateCheckoutLineTotal(i), 0);
  const n = totalAmount != null ? Number(totalAmount) : NaN;
  const footerSubtotal = !Number.isNaN(n) ? n : fromLines;

  let base = footerSubtotal;
  let total = footerSubtotal;
  let fee = 0;
  if (summaryAmounts != null && typeof summaryAmounts === 'object') {
    const b = coerceMoney(summaryAmounts.base);
    const t = coerceMoney(summaryAmounts.total);
    const fExplicit = coerceMoney(summaryAmounts.fee);
    if (Number.isFinite(b)) base = b;
    if (Number.isFinite(t)) total = t;
    if (Number.isFinite(fExplicit) && fExplicit > 0) {
      fee = fExplicit;
    } else if (Number.isFinite(base) && Number.isFinite(t) && t > base + 0.001) {
      fee = round2(t - base);
    }
  }
  const showFee = fee > 0.001;
  if (!showFee) {
    total = footerSubtotal;
    base = footerSubtotal;
  }
  const feeRowLabel = (feeName && String(feeName).trim()) || 'Processing fee';

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Stack spacing={2}>
        {cart.map((item) => {
          const key = `${item.product_id}-${item.variation_id || 0}`;
          const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
          const line = donateCheckoutLineTotal(item);
          return (
            <Stack key={key} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {item.product_name || `Product #${item.product_id}`}
                </Typography>
                {item.variation_name ? (
                  <Typography variant="body2" color="text.secondary">
                    {item.variation_name}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  Qty {qty} × {formatCurrency(Number(item.unit_price || 0))}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 700, flexShrink: 0 }}>
                {formatCurrency(line)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      <Divider sx={{ my: 2 }} />
      {showFee ? (
        <Stack spacing={1.25}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Cart subtotal
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formatCurrency(base)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body1" color="text.secondary">
              {feeRowLabel}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {formatCurrency(fee)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Total
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formatCurrency(total)}
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Cart subtotal
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {formatCurrency(footerSubtotal)}
          </Typography>
        </Stack>
      )}
    </>
  );
}

function DonateCheckoutAdditionalDetailsCard({
  serviceDate,
  serviceTime,
  customerComment,
  onServiceDateChange,
  onServiceTimeChange,
  onCommentChange,
  disabled,
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Additional details
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Stack spacing={1.5} sx={{ flex: '1 1 50%', minWidth: 0 }}>
            <TextField
              label="Date of service"
              type="date"
              value={serviceDate}
              onChange={(e) => onServiceDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={disabled}
            />
            <TextField
              label="Time"
              type="time"
              value={serviceTime}
              onChange={(e) => onServiceTimeChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={disabled}
            />
          </Stack>
          <TextField
            label="Comments (optional)"
            multiline
            minRows={3}
            maxRows={3}
            value={customerComment}
            onChange={(e) => onCommentChange(e.target.value)}
            fullWidth
            size="small"
            disabled={disabled}
            sx={{ flex: '1 1 50%', minWidth: 0 }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function DonateMethodAndFeeCard({ fundingType, onFundingTypeChange, displayPreview, intentErr, disabled, feeDisplay }) {
  const pct = feeDisplay.pct ?? 2.5;
  const feeBlurb =
    feeDisplay.feeCaption && String(feeDisplay.feeCaption).trim()
      ? feeDisplay.feeCaption
      : `Card and digital wallets: ${pct}% processing fee. ACH has no fee.`;
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Payment method
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {feeBlurb}
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <FormControl component="fieldset" disabled={disabled} sx={{ flex: '1 1 auto' }}>
            <RadioGroup value={fundingType} onChange={(e) => onFundingTypeChange(e.target.value)}>
              <FormControlLabel
                value="card_wallet"
                control={<Radio />}
                disabled={disabled}
                label={`Credit / debit card, Apple Pay, Google Pay (${cardFeeRadioSuffix(feeDisplay)})`}
              />
              <FormControlLabel
                value="ach"
                control={<Radio />}
                disabled={disabled}
                label="ACH (US bank account) — no processing fee"
              />
            </RadioGroup>
          </FormControl>
          <Box
            sx={{
              width: { xs: '100%', md: 340 },
              px: 1.5,
              py: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', textAlign: 'right' }}>
                Subtotal (donation) <strong>{formatMoney(displayPreview.base)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', textAlign: 'right' }}>
                {feeDisplay.feeName} <strong>{formatMoney(displayPreview.fee)}</strong>
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.25, fontSize: '1.08rem', textAlign: 'right' }}>
                Total charge: {formatMoney(displayPreview.total)}
              </Typography>
            </Stack>
          </Box>
        </Stack>
        {intentErr ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {intentErr}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}

function PortalSessionCheckoutInner({ checkout, cart, customerComment, serviceDate, serviceTime, onDone, onCheckoutAmountsUpdate, syncPath }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [processingOpen, setProcessingOpen] = React.useState(false);
  const feeSyncTimerRef = React.useRef(null);
  const paymentIntentIdRef = React.useRef('');

  React.useEffect(() => {
    paymentIntentIdRef.current = String(checkout?.paymentIntentId || '');
  }, [checkout?.paymentIntentId]);

  React.useEffect(
    () => () => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
    },
    []
  );

  const queueFeeSyncForElement = React.useCallback(
    (paymentMethodType) => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
      feeSyncTimerRef.current = setTimeout(async () => {
        const pi = paymentIntentIdRef.current;
        if (!pi) return;
        try {
          const res = await post(
            syncPath,
            {
              mode: 'sync-fee',
              syncFee: true,
              paymentIntentId: pi,
              paymentMethodType,
            },
            { timeout: 30000 }
          );
          if (res?.amounts) onCheckoutAmountsUpdate(res.amounts);
        } catch (e) {
          setError(e?.message || 'Could not update fees for the selected payment method');
        }
      }, 400);
    },
    [syncPath, onCheckoutAmountsUpdate]
  );

  const onPay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setProcessingOpen(true);
    setError('');
    try {
      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmErr) {
        setError(confirmErr.message || 'Payment failed');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      if (!paymentIntent || paymentIntent.status !== 'succeeded') {
        setError('Payment was not completed.');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      const fin = await post(
        '/portal/donate/finalize',
        {
          paymentIntentId: paymentIntent.id,
          customerComment,
          serviceDate: serviceDate || undefined,
          serviceTime: serviceTime || undefined,
          items: cart.map((i) => ({
            product_id: i.product_id,
            variation_id: i.variation_id || 0,
            quantity: i.quantity,
          })),
        },
        { timeout: 120000 }
      );
      onDone(fin);
    } catch (e) {
      setError(e?.message || 'Payment failed');
      setProcessingOpen(false);
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={processingOpen} disableEscapeKeyDown onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Payment processing</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="body1">Your payment is in progress. Please wait and do not close this page.</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
      <Stack spacing={2} sx={{ pointerEvents: processingOpen ? 'none' : 'auto', opacity: processingOpen ? 0.6 : 1 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {checkout?.amounts ? (
          <Typography variant="body2">
            Charging <strong>{formatMoney(checkout.amounts.total)}</strong>
            {checkout.amounts.fee > 0 ? ` (includes ${formatMoney(checkout.amounts.fee)} fee)` : ''}.
          </Typography>
        ) : null}
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
          <PaymentElement
            options={{
              wallets: { link: 'never' },
            }}
            onChange={(event) => {
              const v = event.value;
              const typ = v && typeof v.type === 'string' ? v.type : null;
              queueFeeSyncForElement(typ);
            }}
          />
        </Box>
        <Button variant="contained" size="large" onClick={onPay} disabled={busy || !stripe || !elements || processingOpen}>
          {busy ? <CircularProgress size={22} color="inherit" /> : `Donate ${formatCurrency(checkout?.amounts?.total)}`}
        </Button>
      </Stack>
    </>
  );
}

function PublicGuestLinkedCheckoutInner({
  checkout,
  cart,
  billing,
  guest,
  linkToken,
  customerComment,
  serviceDate,
  serviceTime,
  onDone,
  onCheckoutAmountsUpdate,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [processingOpen, setProcessingOpen] = React.useState(false);
  const feeSyncTimerRef = React.useRef(null);
  const paymentIntentIdRef = React.useRef('');

  React.useEffect(() => {
    paymentIntentIdRef.current = String(checkout?.paymentIntentId || '');
  }, [checkout?.paymentIntentId]);

  React.useEffect(
    () => () => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
    },
    []
  );

  const queueFeeSyncForElement = React.useCallback(
    (paymentMethodType) => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
      feeSyncTimerRef.current = setTimeout(async () => {
        const pi = paymentIntentIdRef.current;
        if (!pi) return;
        try {
          const body = {
            mode: 'sync-fee',
            syncFee: true,
            paymentIntentId: pi,
            paymentMethodType,
            guest,
            billing: buildBillingPayload(billing),
          };
          if (!guest && linkToken) {
            body.linkToken = linkToken;
          }
          const res = await post('/public/donate/checkout-intent', body, { timeout: 30000 });
          if (res?.amounts) onCheckoutAmountsUpdate(res.amounts);
        } catch (e) {
          setError(e?.message || 'Could not update fees for the selected payment method');
        }
      }, 400);
    },
    [guest, billing, linkToken, onCheckoutAmountsUpdate]
  );

  const onPay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setProcessingOpen(true);
    setError('');
    try {
      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmErr) {
        setError(confirmErr.message || 'Payment failed');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      if (!paymentIntent || paymentIntent.status !== 'succeeded') {
        setError('Payment was not completed.');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      const fin = await post(
        '/public/donate/finalize',
        {
          paymentIntentId: paymentIntent.id,
          customerComment,
          serviceDate: serviceDate || undefined,
          serviceTime: serviceTime || undefined,
          items: cart.map((i) => ({
            product_id: i.product_id,
            variation_id: i.variation_id || 0,
            quantity: i.quantity,
          })),
          billing: buildBillingPayload(billing),
        },
        { timeout: 120000 }
      );
      onDone(fin);
    } catch (e) {
      setError(e?.message || 'Payment failed');
      setProcessingOpen(false);
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={processingOpen} disableEscapeKeyDown onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Payment processing</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="body1">Your payment is in progress. Please wait and do not close this page.</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
      <Stack spacing={2} sx={{ pointerEvents: processingOpen ? 'none' : 'auto', opacity: processingOpen ? 0.6 : 1 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {checkout?.amounts ? (
          <Typography variant="body2">
            Charging <strong>{formatMoney(checkout.amounts.total)}</strong>
            {checkout.amounts.fee > 0 ? ` (includes ${formatMoney(checkout.amounts.fee)} fee)` : ''}.
          </Typography>
        ) : null}
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
          <PaymentElement
            options={{ wallets: { link: 'never' } }}
            onChange={(event) => {
              const v = event.value;
              const typ = v && typeof v.type === 'string' ? v.type : null;
              queueFeeSyncForElement(typ);
            }}
          />
        </Box>
        <Button variant="contained" size="large" onClick={onPay} disabled={busy || !stripe || !elements || processingOpen}>
          {busy ? <CircularProgress size={22} color="inherit" /> : `Donate ${formatCurrency(checkout?.amounts?.total)}`}
        </Button>
      </Stack>
    </>
  );
}

export default function PublicDonateCheckoutPage() {
  const navigate = useNavigate();
  const { cart, totalAmount, clearCart, donateMode, linkToken, verifiedEmail, setGuestMode } = usePublicDonate();
  const { isAuthenticated, loading: authLoading, staff } = useAuth();
  const hasPortalSession = !authLoading && isAuthenticated && staff?.portal;

  const [billing, setBilling] = React.useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'US',
  });
  /** choose → billing → method → pay. Linked donors skip choose. */
  const [step, setStep] = React.useState(() => (donateMode === 'linked' ? 'billing' : 'choose'));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [checkout, setCheckout] = React.useState(null);
  const [stripePromise, setStripePromise] = React.useState(null);
  const [fundingType, setFundingType] = React.useState('card_wallet');
  const [portalPayStep, setPortalPayStep] = React.useState('method');
  const [portalIntentErr, setPortalIntentErr] = React.useState('');
  const [serverFeeUi, setServerFeeUi] = React.useState(null);
  const [serviceDate, setServiceDate] = React.useState('');
  const [serviceTime, setServiceTime] = React.useState('');
  const [customerComment, setCustomerComment] = React.useState('');
  const [signInDialogOpen, setSignInDialogOpen] = React.useState(false);
  const [guestCheckoutRecaptchaToken, setGuestCheckoutRecaptchaToken] = React.useState(null);
  const guestCheckoutRecaptchaRef = React.useRef(null);

  React.useEffect(() => {
    if (hasPortalSession) setSignInDialogOpen(false);
  }, [hasPortalSession]);

  React.useEffect(() => {
    if (donateMode !== 'guest') {
      setGuestCheckoutRecaptchaToken(null);
      try {
        guestCheckoutRecaptchaRef.current?.reset();
      } catch {
        /* ignore */
      }
    }
  }, [donateMode]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await get('/public/donate/fee-ui', {});
        if (!cancelled && r?.feeUi) setServerFeeUi(r.feeUi);
      } catch {
        /* build-time REACT_APP_* only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cartItemsPayload = React.useMemo(
    () =>
      cart.map((i) => ({
        product_id: i.product_id,
        variation_id: i.variation_id || 0,
        quantity: i.quantity,
      })),
    [cart]
  );

  const cartItemsKey = React.useMemo(() => JSON.stringify(cartItemsPayload), [cartItemsPayload]);

  React.useEffect(() => {
    if (verifiedEmail) {
      setBilling((b) => ({ ...b, email: verifiedEmail }));
    }
  }, [verifiedEmail]);

  React.useEffect(() => {
    if (!hasPortalSession) return;
    setPortalPayStep('method');
    setCheckout(null);
    setStripePromise(null);
    setPortalIntentErr('');
  }, [cartItemsKey, hasPortalSession]);

  if (authLoading) {
    return (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const baseAmount = round2(Number(totalAmount || 0));
  const feeDisplay = React.useMemo(() => mergedFeeDisplay(serverFeeUi, checkout?.feeUi), [serverFeeUi, checkout?.feeUi]);
  const preview = React.useMemo(
    () => computeClientFeeTotal(baseAmount, fundingType, feeDisplay),
    [baseAmount, fundingType, feeDisplay]
  );
  const displayPreviewPublic =
    step === 'pay' && checkout?.amounts
      ? { base: checkout.amounts.base, fee: checkout.amounts.fee, total: checkout.amounts.total }
      : preview;
  const displayPreviewPortal =
    portalPayStep === 'pay' && checkout?.amounts
      ? { base: checkout.amounts.base, fee: checkout.amounts.fee, total: checkout.amounts.total }
      : preview;

  const onCheckoutAmountsUpdate = React.useCallback((amounts) => {
    setCheckout((c) => (c ? { ...c, amounts } : c));
  }, []);

  if (!cart.length) {
    return <Navigate to="/donate/cart" replace />;
  }

  const onDone = (fin) => {
    const oid = fin?.orderId || '';
    navigate(`/donate/confirmation/${oid}`, {
      replace: true,
      state: { checkout: fin },
    });
    // Defer clearCart: clearing first re-renders checkout with empty cart and triggers
    // `<Navigate to="/donate/cart" />` before the router applies this navigation.
    window.setTimeout(() => {
      clearCart();
    }, 0);
  };

  const setField = (k) => (e) => setBilling((b) => ({ ...b, [k]: e.target.value }));

  const startPortalPaymentIntent = async () => {
    setLoading(true);
    setPortalIntentErr('');
    setError('');
    try {
      const out = await post(
        '/portal/donate/checkout-intent',
        {
          items: cartItemsPayload,
          fundingType,
        },
        { timeout: 60000 }
      );
      setCheckout(out);
      setStripePromise(loadStripe(out.publishableKey));
      setPortalPayStep('pay');
    } catch (e) {
      setPortalIntentErr(e?.message || 'Failed to initialize checkout');
    } finally {
      setLoading(false);
    }
  };

  if (hasPortalSession) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Donate checkout
        </Typography>
        <Alert severity="info">
          You are signed in to the portal. Payment and order use your account on file.
        </Alert>
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Order summary
              </Typography>
              <Button variant="outlined" size="small" component={NavLink} to="/donate/cart">
                Back to cart
              </Button>
            </Stack>
            <DonateCheckoutOrderLines
              cart={cart}
              totalAmount={totalAmount}
              summaryAmounts={portalPayStep === 'method' || portalPayStep === 'pay' ? displayPreviewPortal : undefined}
              feeName={feeDisplay.feeName}
            />
          </CardContent>
        </Card>

        <DonateCheckoutAdditionalDetailsCard
          serviceDate={serviceDate}
          serviceTime={serviceTime}
          customerComment={customerComment}
          onServiceDateChange={setServiceDate}
          onServiceTimeChange={setServiceTime}
          onCommentChange={setCustomerComment}
          disabled={loading}
        />

        {portalPayStep === 'method' ? (
          <>
            <DonateMethodAndFeeCard
              fundingType={fundingType}
              onFundingTypeChange={setFundingType}
              displayPreview={displayPreviewPortal}
              intentErr={portalIntentErr}
              disabled={loading}
              feeDisplay={feeDisplay}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={startPortalPaymentIntent} disabled={loading}>
                {loading ? 'Preparing payment…' : 'Continue to payment'}
              </Button>
            </Stack>
          </>
        ) : null}

        {portalPayStep === 'pay' && checkout?.clientSecret && stripePromise ? (
          <Card variant="outlined">
            <CardContent>
              <Elements stripe={stripePromise} options={{ clientSecret: checkout.clientSecret }}>
                <PortalSessionCheckoutInner
                  checkout={checkout}
                  cart={cart}
                  customerComment={customerComment}
                  serviceDate={serviceDate}
                  serviceTime={serviceTime}
                  onDone={onDone}
                  onCheckoutAmountsUpdate={onCheckoutAmountsUpdate}
                  syncPath="/portal/donate/checkout-intent"
                />
              </Elements>
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    );
  }

  const onBillingContinue = () => {
    if (!cart.length) {
      setError('Your cart is empty.');
      return;
    }
    setError('');
    setStep('method');
  };

  const startPublicPaymentIntent = async () => {
    setLoading(true);
    setError('');
    try {
      if (donateMode === 'guest' && RECAPTCHA_ENABLED && !guestCheckoutRecaptchaToken) {
        setError('Please complete the reCAPTCHA verification before continuing to payment.');
        setLoading(false);
        return;
      }
      const payload = {
        items: cartItemsPayload,
        guest: donateMode === 'guest',
        billing: buildBillingPayload(billing),
        fundingType,
      };
      if (donateMode === 'linked') {
        payload.linkToken = linkToken;
      }
      if (donateMode === 'guest' && guestCheckoutRecaptchaToken) {
        payload.recaptchaToken = guestCheckoutRecaptchaToken;
      }
      const out = await post('/public/donate/checkout-intent', payload, { timeout: 60000 });
      setCheckout(out);
      setStripePromise(loadStripe(out.publishableKey));
      setStep('pay');
      setGuestCheckoutRecaptchaToken(null);
      try {
        guestCheckoutRecaptchaRef.current?.reset();
      } catch {
        /* ignore */
      }
    } catch (e) {
      setError(e?.message || 'Failed to initialize checkout');
      try {
        guestCheckoutRecaptchaRef.current?.reset();
      } catch {
        /* ignore */
      }
      setGuestCheckoutRecaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'choose') {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Donate checkout
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Order summary
              </Typography>
              <Button variant="outlined" size="small" component={NavLink} to="/donate/cart">
                Back to cart
              </Button>
            </Stack>
            <DonateCheckoutOrderLines cart={cart} totalAmount={totalAmount} feeName={feeDisplay.feeName} />
          </CardContent>
        </Card>
        <DonateCheckoutAdditionalDetailsCard
          serviceDate={serviceDate}
          serviceTime={serviceTime}
          customerComment={customerComment}
          onServiceDateChange={setServiceDate}
          onServiceTimeChange={setServiceTime}
          onCommentChange={setCustomerComment}
          disabled={false}
        />
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                How would you like to check out?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in with your email, or continue as a guest?
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" color="primary" size="large" onClick={() => setSignInDialogOpen(true)}>
                  Sign in
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => {
                    setGuestMode();
                    setStep('billing');
                  }}
                >
                  Continue as guest
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        <DonateCheckoutSignInDialog open={signInDialogOpen} onClose={() => setSignInDialogOpen(false)} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Donate checkout
      </Typography>
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Order summary
            </Typography>
            <Button variant="outlined" size="small" component={NavLink} to="/donate/cart">
              Back to cart
            </Button>
          </Stack>
          <DonateCheckoutOrderLines
            cart={cart}
            totalAmount={totalAmount}
            summaryAmounts={step === 'method' || step === 'pay' ? displayPreviewPublic : undefined}
            feeName={feeDisplay.feeName}
          />
        </CardContent>
      </Card>

      <DonateCheckoutAdditionalDetailsCard
        serviceDate={serviceDate}
        serviceTime={serviceTime}
        customerComment={customerComment}
        onServiceDateChange={setServiceDate}
        onServiceTimeChange={setServiceTime}
        onCommentChange={setCustomerComment}
        disabled={loading}
      />

      {step === 'billing' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {donateMode === 'linked'
                  ? 'Billing email must match your verified portal account email.'
                  : 'Enter the name and address that should appear on the receipt.'}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="First name" value={billing.first_name} onChange={setField('first_name')} required fullWidth size="small" />
                <TextField label="Last name" value={billing.last_name} onChange={setField('last_name')} required fullWidth size="small" />
              </Stack>
              <TextField
                label="Email"
                type="email"
                value={billing.email}
                onChange={setField('email')}
                required
                fullWidth
                size="small"
                disabled={donateMode === 'linked' && !!verifiedEmail}
                helperText={donateMode === 'linked' ? 'Must match the email you verified.' : undefined}
              />
              <TextField label="Phone" value={billing.phone} onChange={setField('phone')} fullWidth size="small" />
              <TextField label="Street address" value={billing.address_1} onChange={setField('address_1')} required fullWidth size="small" />
              <TextField label="Apt / suite (optional)" value={billing.address_2} onChange={setField('address_2')} fullWidth size="small" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="City" value={billing.city} onChange={setField('city')} required fullWidth size="small" />
                <TextField label="State" value={billing.state} onChange={setField('state')} required fullWidth size="small" />
                <TextField label="ZIP" value={billing.postcode} onChange={setField('postcode')} required fullWidth size="small" />
              </Stack>
              <TextField label="Country" value={billing.country} onChange={setField('country')} fullWidth size="small" />
              {error ? <Alert severity="error">{error}</Alert> : null}
              <Button variant="contained" onClick={onBillingContinue}>
                Continue
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {step === 'method' ? (
        <>
          <DonateMethodAndFeeCard
            fundingType={fundingType}
            onFundingTypeChange={setFundingType}
            displayPreview={displayPreviewPublic}
            intentErr={error}
            disabled={loading}
            feeDisplay={feeDisplay}
          />
          {donateMode === 'guest' && RECAPTCHA_ENABLED ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <ReCAPTCHA
                ref={guestCheckoutRecaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => {
                  setGuestCheckoutRecaptchaToken(token);
                  setError('');
                }}
                onExpired={() => setGuestCheckoutRecaptchaToken(null)}
                onError={() => {
                  setGuestCheckoutRecaptchaToken(null);
                  setError('reCAPTCHA error. Please refresh the page and try again.');
                }}
              />
            </Box>
          ) : null}
          <Divider />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" onClick={() => setStep('billing')}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={startPublicPaymentIntent}
              disabled={loading || (donateMode === 'guest' && RECAPTCHA_ENABLED && !guestCheckoutRecaptchaToken)}
            >
              {loading ? 'Preparing payment…' : 'Continue to payment'}
            </Button>
          </Stack>
        </>
      ) : null}

      {step === 'pay' && checkout?.clientSecret && stripePromise ? (
        <Card variant="outlined">
          <CardContent>
            <Elements stripe={stripePromise} options={{ clientSecret: checkout.clientSecret }}>
              <PublicGuestLinkedCheckoutInner
                checkout={checkout}
                cart={cart}
                billing={billing}
                guest={donateMode === 'guest'}
                linkToken={donateMode === 'linked' ? linkToken : undefined}
                customerComment={customerComment}
                serviceDate={serviceDate}
                serviceTime={serviceTime}
                onDone={onDone}
                onCheckoutAmountsUpdate={onCheckoutAmountsUpdate}
              />
            </Elements>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
}
